/** STILGIA Shop — informational page. Beta wording until SHOP_PUBLIC is flipped. */
import { useEffect, useState } from 'react';
import { ShoppingBag, Camera, Search, CreditCard, Truck } from 'lucide-react';
import { useWww } from './_layout';
import { wwwTheme, wwwRadius, wwwFonts, wwwAccent } from '@/src/website/theme';
import { SeoHead } from '@/src/website/SeoHead';
import { SHOP_PUBLIC, SHOP_STORE_URL } from '@/src/website/launch';

function useWindowDimensions() {
  const [d, setD] = useState({ width: typeof window !== 'undefined' ? window.innerWidth : 1200 });
  useEffect(() => {
    const onR = () => setD({ width: window.innerWidth });
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, []);
  return d;
}

const LEGAL_LINKS: Array<{ key: 'shopLegalTerms' | 'shopLegalShipping' | 'shopLegalReturns' | 'shopLegalNotice' | 'shopLegalContact' | 'shopLegalPrivacy'; path: string }> = [
  { key: 'shopLegalTerms', path: '/policies/terms-of-service' },
  { key: 'shopLegalShipping', path: '/policies/shipping-policy' },
  { key: 'shopLegalReturns', path: '/policies/refund-policy' },
  { key: 'shopLegalNotice', path: '/policies/legal-notice' },
  { key: 'shopLegalContact', path: '/policies/contact-information' },
  { key: 'shopLegalPrivacy', path: '/policies/privacy-policy' },
];

export default function Shop() {
  const { t, lang } = useWww();
  const { width } = useWindowDimensions();
  const isMobile = width < 760;

  const cards = [
    { icon: ShoppingBag, title: t.shopCatalogTitle, body: t.shopCatalogBody },
    { icon: Camera, title: t.shopTryonTitle, body: t.shopTryonBody },
    { icon: Search, title: t.shopSimilarTitle, body: t.shopSimilarBody },
    { icon: CreditCard, title: t.shopBuyTitle, body: t.shopBuyBody },
  ];

  useEffect(() => { document.title = t.shopTitle + ' · STILGIA'; }, [t.shopTitle, lang]);

  return (
    <>
      <SeoHead page="shop" locale={lang} />
      <div data-testid="shop-page" style={{ maxWidth: 1080, marginLeft: 'auto', marginRight: 'auto', paddingLeft: isMobile ? 20 : 48, paddingRight: isMobile ? 20 : 48, paddingTop: 64, paddingBottom: 96, boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <h1 style={{ color: wwwTheme.text, fontFamily: wwwFonts.serifBold, fontSize: isMobile ? 34 : 50, lineHeight: isMobile ? '40px' : '56px', marginTop: 0, marginBottom: 0 }}>{t.shopTitle}</h1>
          {!SHOP_PUBLIC && (
            <span data-testid="shop-beta-badge" style={{ color: wwwAccent, fontSize: 12, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', borderColor: wwwAccent, borderWidth: 1, borderStyle: 'solid', borderRadius: wwwRadius.pill, paddingLeft: 10, paddingRight: 10, paddingTop: 3, paddingBottom: 3 }}>{t.shopBetaBadge}</span>
          )}
        </div>
        <p data-testid="shop-intro" style={{ color: wwwTheme.muted, fontSize: isMobile ? 16 : 18, lineHeight: '26px', marginTop: 16, marginBottom: 0, maxWidth: 760 }}>
          {SHOP_PUBLIC ? t.shopIntroPublic : t.shopIntroBeta}
        </p>

        <div data-testid="shop-shipping-note" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 24, borderColor: wwwTheme.border, borderWidth: 1, borderStyle: 'solid', borderRadius: wwwRadius.lg, padding: 14, maxWidth: 760, boxSizing: 'border-box' }}>
          <Truck size={18} color={wwwAccent} strokeWidth={1.6} style={{ flexShrink: 0 }} />
          <span style={{ color: wwwTheme.text, fontSize: 14 }}>{t.shopShippingNote}</span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 18, marginTop: 40 }}>
          {cards.map((c, i) => (
            <div key={i} data-testid={`shop-card-${i}`} style={{ borderColor: wwwTheme.border, borderWidth: 1, borderStyle: 'solid', borderRadius: wwwRadius.lg, padding: 24, boxSizing: 'border-box' }}>
              <c.icon size={22} color={wwwAccent} strokeWidth={1.6} />
              <h2 style={{ color: wwwTheme.text, fontFamily: wwwFonts.serifBold, fontSize: 19, marginTop: 14, marginBottom: 0 }}>{c.title}</h2>
              <p style={{ color: wwwTheme.muted, fontSize: 14, lineHeight: '22px', marginTop: 10, marginBottom: 0 }}>{c.body}</p>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 40, display: 'flex', flexDirection: 'column', alignItems: SHOP_PUBLIC ? 'center' : 'flex-start', gap: 10 }}>
          <a href={SHOP_STORE_URL} target="_blank" rel="noopener noreferrer" data-testid="shop-store-cta" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 10, backgroundColor: SHOP_PUBLIC ? wwwTheme.text : 'transparent', color: SHOP_PUBLIC ? wwwTheme.bg : wwwTheme.text, borderColor: wwwTheme.border, borderWidth: 1, borderStyle: 'solid', borderRadius: wwwRadius.pill, paddingLeft: 22, paddingRight: 22, paddingTop: 12, paddingBottom: 12, fontSize: 14, fontWeight: 600 }}>
            <ShoppingBag size={16} strokeWidth={1.8} />
            {t.shopCta}
          </a>
        </div>

        <div style={{ marginTop: 48, borderTopColor: wwwTheme.border, borderTopWidth: 1, borderTopStyle: 'solid', paddingTop: 24 }}>
          <p style={{ color: wwwTheme.muted, fontSize: 12, fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase', marginTop: 0, marginBottom: 12 }}>{t.shopLegalTitle}</p>
          <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 16 }}>
            {LEGAL_LINKS.map(l => (
              <a key={l.key} href={`${SHOP_STORE_URL}${l.path}`} target="_blank" rel="noopener noreferrer" data-testid={`shop-legal-${l.key}`} style={{ color: wwwTheme.muted, fontSize: 13, textDecoration: 'underline' }}>{t[l.key]}</a>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
